import React, { useEffect, useState } from 'react';
import {
  Button,
  DateRangeType,
  ENUM_DATE_RANGE,
  Paginate,
  getDateRangeSelector
} from '@cokitchen/cokitchen-components';
import TripHistoryCard from './trip-history-card';
import './styles.scss';
import { ExtendedFilterType } from './types';
import { TripTab } from './triptab';
import { StatusFilterConfig } from './filter-config';
import ExportTripHistoryModal from './export-modal';
import { ENUM_TRIP_STATUS } from 'utils/enum';
import { TripHistoryType } from 'models/trip-history.model';
import { apiService } from 'utils/api';
import { OrderType } from 'models/order.model';
import { ExportIcon } from 'cokitchen-icons';
import Loader from 'components/base/loader';

const tripOptions: ExtendedFilterType[] = [
  'All',
  ENUM_TRIP_STATUS.COMPLETED,
  ENUM_TRIP_STATUS.FAILED
];

const TripHistory = ({ riderId, partnerId }: { riderId: string; partnerId: string }) => {
  const [tripDetails, setTripDetails] = useState<TripHistoryType[]>([]);
  const [activeTab, setActiveTab] = useState<ExtendedFilterType>('All');
  const [filteredTrips, setFilteredTrips] = useState(tripDetails as TripHistoryType[]);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showPagination, setShowPagination] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeType>({
    range: ENUM_DATE_RANGE.ALL,
    date: getDateRangeSelector(ENUM_DATE_RANGE.ALL)
  });
  const loadTrips = async (pageNo = 1) => {
    setLoading(true);
    const { startDate: start_time, endDate: end_time } = dateRange.date;

    const res = await apiService.get(`logistics/${partnerId}/riders/${riderId}/trips`, {
      params: {
        page_no: pageNo,
        start_time,
        end_time
      }
    });
    const data =
      res.data.trips?.results?.map((order: OrderType) => ({
        id: order.id,
        amount: order.calculated_order.total_amount,
        order_code: order.order_code,
        status: order.completed,
        date: order.kitchen_completed_time,
        address: order.calculated_order.address_details.address_line,
        deduction: 0
      })) ?? [];
    const pageCount = Math.ceil(res?.data?.trips?.total / 10);
    setPageCount(pageCount);
    setTripDetails(data);
    setLoading(false);
  };
  const onClose = () => {
    setShowExportModal(false);
  };

  useEffect(() => {
    const filtered = StatusFilterConfig(tripDetails, activeTab);
    setFilteredTrips(filtered);
    setShowPagination(filtered.length > 0);
  }, [activeTab, tripDetails]);

  useEffect(() => {
    setShowPagination(false);
    loadTrips();
  }, [dateRange, riderId]);

  useEffect(() => {
    setShowPagination(true);
  }, [pageCount]);

  const getSelectedPage = (val: { selected: number }) => {
    const pageNo = val?.selected + 1;
    loadTrips(pageNo);
  };

  return (
    <div className='trip-history'>
      <div className='flex flex-wrap gap-2 justify-between items-center mb-4'>
        <h3 className='text-gray-6 font-medium text-xl whitespace-nowrap'>Trips history</h3>
        <div className='export-button'>
          <Button small variant='block' color='primary' onClick={() => setShowExportModal(true)}>
            <ExportIcon className='mr-1' />
            Export
          </Button>
        </div>
      </div>
      <TripTab
        list={tripOptions}
        active={activeTab}
        setActive={setActiveTab}
        dateRange={dateRange}
        setDateRange={setDateRange}
      />
      {loading ? (
        <Loader className='mt-[100px] h-[200px]' />
      ) : filteredTrips.length === 0 ? (
        <h4 className='font-medium text-center mt-[100px]'>
          There are no trips to show for
          <span className='ml-1 lowercase'>
            {dateRange.range === ENUM_DATE_RANGE.SELECT ? 'selected date range' : dateRange.range}
          </span>
        </h4>
      ) : (
        <div className='gap-4 mt-8 grid md:grid-cols-2'>
          {filteredTrips
            .sort((a, b) => +b.date - +a.date)
            .map((detail, i) => (
              <TripHistoryCard key={i} tripHistory={detail} />
            ))}
        </div>
      )}
      {showPagination && <Paginate onPageChange={getSelectedPage} pageCount={pageCount} />}
      <ExportTripHistoryModal
        onClose={onClose}
        show={showExportModal}
        rider={{ riderId, partnerId }}
      />
    </div>
  );
};

export default TripHistory;
